import React, { useEffect, Suspense } from 'react';
import {
  BrowserRouter as Router, Routes, Route, Navigate, useLocation, createRoutesFromChildren, matchRoutes,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuth } from './context/AuthContext';

import LandingPage from './pages/LandingPage';
import { ARTICLE_SLUGS, AUTHOR_IDS } from './content/articleRoutes';
import AdminRoute from './components/AdminRoute';
import RequireChosenOption from './components/routing/RequireChosenOption';
import CampaignLandingPage from './pages/campaigns/CampaignLandingPage';

import RetroactiveConsentModal from './components/RetroactiveConsentModal';
import ConsentBanner from './components/consent/ConsentBanner';
import { useConsent } from './components/consent/consentClient';
import ToastIcon from './components/ui/ToastIcon';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ChatWidget from './components/chat/ChatWidget';
import { PageHelpProvider } from './context/PageHelpProvider';
import { trackPageView } from './utils/analytics';
import { onLeadRouteChange, linkLeadToUser } from './utils/leadAttribution';
import { lazyPage } from './routing/lazyPage';
import ChunkErrorBoundary from './routing/ChunkErrorBoundary';
// Shared stylesheet, kept in the main file on purpose: besides the admin panel it
// also styles the `.badge` labels on the dashboard and the template picker, so it
// must load exactly where it did before the admin screens were split out.
import './pages/admin/admin.css';

// Pages. The homepage (and the /g/ campaign copies of it) is in the main file: it is
// the page most visitors and every advert land on. Every other screen is its own
// file, downloaded the first time it is opened (lazyPage.js), so the homepage and
// the guides no longer carry the editor, checkout and admin panel (SEO phase 0,
// section 6 item 1; it was one 1.7 MB file). The route guards stay here: they are
// small and decide where a visitor goes before any screen loads.
const NotFoundPage = lazyPage(() => import('./pages/NotFoundPage'));
const GuidePage = lazyPage(() => import('./pages/GuidePage'));
const LoginPage = lazyPage(() => import('./pages/LoginPage'));
const RegisterPage = lazyPage(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazyPage(() => import('./pages/VerifyEmailPage'));
const OAuthCallbackPage = lazyPage(() => import('./pages/OAuthCallbackPage'));
const ForgotPasswordPage = lazyPage(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyPage(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'));
const ProviderSignupPage = lazyPage(() => import('./pages/ProviderSignupPage'));
const NewProjectPage = lazyPage(() => import('./pages/NewProjectPage'));
const NewVideoChooserPage = lazyPage(() => import('./pages/NewVideoChooserPage'));
const TeamReviewPage = lazyPage(() => import('./pages/TeamReviewPage'));
const PaymentPage = lazyPage(() => import('./pages/PaymentPage'));
const DownloadPage = lazyPage(() => import('./pages/DownloadPage'));
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'));
const SharePage = lazyPage(() => import('./pages/SharePage'));
const FeedbackPage = lazyPage(() => import('./pages/FeedbackPage'));
const AdminLayout = lazyPage(() => import('./pages/admin/AdminLayout'));
const UsersPage = lazyPage(() => import('./pages/admin/UsersPage'));
const UserDetailPage = lazyPage(() => import('./pages/admin/UserDetailPage'));
const CouponsReferralsPage = lazyPage(() => import('./pages/admin/CouponsReferralsPage'));
const AdminDashboardPage = lazyPage(() => import('./pages/admin/DashboardPage'));
const BillingPage = lazyPage(() => import('./pages/admin/BillingPage'));
const MarketingPage = lazyPage(() => import('./pages/admin/MarketingPage'));
const HealthPage = lazyPage(() => import('./pages/admin/HealthPage'));
const DataManagementPage = lazyPage(() => import('./pages/admin/DataManagementPage'));
const B2BPreviewPage = lazyPage(() => import('./pages/admin/B2BPreviewPage'));
const AuditLogPage = lazyPage(() => import('./pages/admin/AuditLogPage'));
const OrdersPage = lazyPage(() => import('./pages/admin/OrdersPage'));
const LeadsPage = lazyPage(() => import('./pages/admin/LeadsPage'));
const LeadDetailPage = lazyPage(() => import('./pages/admin/LeadDetailPage'));
const RestorationPage = lazyPage(() => import('./pages/admin/RestorationPage'));
const PrivacyPolicyPage = lazyPage(() => import('./pages/PrivacyPolicyPage'));
const CrossBorderDisclosurePage = lazyPage(() => import('./pages/CrossBorderDisclosurePage'));
const CookiePolicyPage = lazyPage(() => import('./pages/CookiePolicyPage'));
const RefundPolicyPage = lazyPage(() => import('./pages/RefundPolicyPage'));
const EditorialStandardsPage = lazyPage(() => import('./pages/EditorialStandardsPage'));
const DataDeletionPage = lazyPage(() => import('./pages/DataDeletionPage'));
const PartnerAgreementPage = lazyPage(() => import('./pages/PartnerAgreementPage'));
const RefundSurveyPage = lazyPage(() => import('./pages/RefundSurveyPage'));
const DataDeletionStatusPage = lazyPage(() => import('./pages/DataDeletionStatusPage'));
const DataDeletionConfirmPage = lazyPage(() => import('./pages/DataDeletionConfirmPage'));
const TermsPage = lazyPage(() => import('./pages/TermsPage'));
const PartnersPage = lazyPage(() => import('./pages/PartnersPage'));
const PartnerGuidePage = lazyPage(() => import('./pages/PartnerGuidePage'));
const PartnerFeedbackPage = lazyPage(() => import('./pages/PartnerFeedbackPage'));
const UnsubscribePage = lazyPage(() => import('./pages/UnsubscribePage'));
const DevTemplateCatalogue = lazyPage(() => import('./pages/dev/DevTemplateCatalogue'));

// The content hub: the article screen (with its markdown renderer and the guide index)
// was the first part of the site to be split out like this. Each guide is its own
// screen too, downloading the shared article screen plus only that guide's prose
// (content/article-text/<slug>.json, written by scripts/build-content.js). Before,
// every guide page downloaded every guide's text, which grows with each new guide.
// Being an ordinary lazyPage, a guide gets the same preload before the first render,
// retry and "This page didn't load" handling as every other screen.
const articleScreen = (slug) => lazyPage(() => Promise.all([
  import('./pages/ArticlePage'),
  import(`./content/article-text/${slug}.json`),
]).then(([page, text]) => {
  const ArticlePage = page.default;
  const prose = text.default || text;
  const GuideScreen = (props) => <ArticlePage {...props} text={prose} />;
  return { default: GuideScreen };
}));
const GUIDE_SCREENS = Object.fromEntries(ARTICLE_SLUGS.map(slug => [slug, articleScreen(slug)]));
const ArticlesIndexPage = lazyPage(() => import('./pages/ArticlesIndexPage'));
const AuthorPage = lazyPage(() => import('./pages/AuthorPage'));

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, isGuest, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingSpinner fullScreen />;
  if (!isAuthenticated || isGuest) {
    const next = encodeURIComponent(location.pathname + location.search);
    // A guest needs a real account to reach these pages → send to sign-up (the
    // upgrade path). Anyone with no session at all is most likely a returning
    // user whose session lapsed → send to sign-in, which carries ?next and has
    // a "Create one" link for genuine first-timers.
    const dest = isGuest ? 'register' : 'login';
    return <Navigate to={`/${dest}?next=${next}`} replace />;
  }
  return children;
};
PrivateRoute.gatesScreens = true;

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isGuest, loading } = useAuth();
  if (loading) return <LoadingSpinner fullScreen />;
  // Guest users are technically authenticated but should still be able to
  // access /login and /register so they can create a real account.
  return (isAuthenticated && !isGuest) ? <Navigate to="/dashboard" replace /> : children;
};

// Guards project/payment/download pages. Any user with an existing session
// (real account or guest who came through the register consent flow) is let
// through. Visitors with no session are sent to sign-in (?next carries them
// back); the login page links to sign-up and guest for first-timers.
const ProjectRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingSpinner fullScreen />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
};
ProjectRoute.gatesScreens = true;


const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Explicitly instant. `html` now sets `scroll-behavior: smooth` so in-page
    // anchors glide, but a route change is not a scroll — without this the new
    // page would start at the old offset and animate upward.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    trackPageView(pathname);
    // Outreach attribution — a no-op unless this visitor came from one of our
    // emails AND has accepted Analytics cookies (see utils/leadAttribution.js).
    onLeadRouteChange(pathname);
  }, [pathname]);
  return null;
};

// Outreach attribution: when a visitor who arrived from one of our outreach
// emails (and accepted Analytics cookies) is signed in — by registering, logging
// in, Google/Facebook, guest or the demo — link that account to their lead once.
const LeadAttributionLinker = () => {
  const { user } = useAuth();
  const { consent } = useConsent();
  const userId = user?.id || user?._id || null;
  useEffect(() => {
    if (userId && consent?.analytics) linkLeadToUser(String(userId));
  }, [userId, consent?.analytics]);
  return null;
};

const APP_ROUTES = (
  <Routes>
    {/* Public routes */}
    <Route path="/" element={<LandingPage />} />

    {/* Google Ads / paid-traffic landing pages, /g/<campaign>. Each is a copy of the
        main landing page at its own URL so each campaign is tracked separately in
        analytics. These are kept OUT of the search index by an
        `X-Robots-Tag: noindex, nofollow` response header served for /g/(.*) (see
        vercel.json), backed up by the noindex meta CampaignLandingPage injects.
        robots.txt deliberately does NOT Disallow /g/ — crawling has to be allowed for
        a crawler to fetch the page and honour the noindex at all; a Disallow would
        leave the URL eligible to appear as a bare link. See CLAUDE.md for the rule. */}
    <Route path="/g/plaunch" element={<CampaignLandingPage />} />
    {/* "Helpful hints" was merged into the guide (28 Sep 2026). vercel.json also
        redirects /get-started at the edge; this covers in-app navigation. */}
    <Route path="/get-started" element={<Navigate to="/guide#before-you-start" replace />} />
    <Route path="/guide" element={<GuidePage />} />

    {/* Content hub. One explicit route per article rather than a `/:slug` catch-all,
        so a new article can never shadow an existing page. Generated from the same
        manifest the pages read, and prerendered to static HTML at build time (see
        scripts/prerender.mjs) because crawlers and link-preview scrapers cannot be
        relied on to run the bundle. */}
    <Route path="/articles" element={<ArticlesIndexPage />} />
    {ARTICLE_SLUGS.map(slug => {
      const GuideScreen = GUIDE_SCREENS[slug];
      return <Route key={slug} path={`/${slug}`} element={<GuideScreen slug={slug} />} />;
    })}
    {/* Author pages (SEO phase 1): one explicit route per author, like the articles,
        generated from the same manifest and prerendered by scripts/prerender.mjs. */}
    {AUTHOR_IDS.map(id => (
      <Route key={`author-${id}`} path={`/authors/${id}`} element={<AuthorPage id={id} />} />
    ))}
    {process.env.NODE_ENV !== 'production' && (
      <Route path="/dev/template-catalogue" element={<DevTemplateCatalogue />} />
    )}
    <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
    <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/verify-email" element={<VerifyEmailPage />} />
    <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
    <Route path="/privacy" element={<PrivacyPolicyPage />} />
    <Route path="/privacy/cross-border-disclosure" element={<CrossBorderDisclosurePage />} />
    <Route path="/privacy/cookie-policy" element={<CookiePolicyPage />} />
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/refund-policy" element={<RefundPolicyPage />} />
    <Route path="/editorial-standards" element={<EditorialStandardsPage />} />
    <Route path="/data-deletion" element={<DataDeletionPage />} />
    {/* Facebook data deletion: status page Facebook links to, and the emailed one-click
        "delete my whole account" link. Both noindex (vercel.json header too). */}
    <Route path="/data-deletion/status" element={<DataDeletionStatusPage />} />
    <Route path="/data-deletion/confirm/:token" element={<DataDeletionConfirmPage />} />
    <Route path="/partners/agreement" element={<PartnerAgreementPage />} />
    {/* Private, unlinked refund survey sent by our team (noindex; vercel.json header too). */}
    <Route path="/support/feedback/:token" element={<RefundSurveyPage />} />
    <Route path="/partners" element={<PartnersPage />} />
    {/* Contact Us — single destination; legacy /support redirects here */}
    <Route path="/support" element={<Navigate to="/contact" replace />} />

    {/* Protected routes */}
    <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
    <Route path="/dashboard/partner" element={<PrivateRoute><PartnerGuidePage /></PrivateRoute>} />
    <Route path="/partners/feedback" element={<PrivateRoute><PartnerFeedbackPage /></PrivateRoute>} />
    <Route path="/unsubscribe" element={<UnsubscribePage />} />
    <Route path="/provider-signup" element={<PrivateRoute><ProviderSignupPage /></PrivateRoute>} />
    {/* Distinct URL for a confirmed submission (not just reaching the form) —
        lets analytics tell "started" and "completed" apart. Same component,
        same confirmation UI; see ProviderSignupPage.js's submittedRoute prop. */}
    <Route path="/provider-signup/submitted" element={<PrivateRoute><ProviderSignupPage submittedRoute /></PrivateRoute>} />
    <Route path="/projects/choose" element={<PrivateRoute><NewVideoChooserPage /></PrivateRoute>} />
    <Route path="/projects/new" element={<PrivateRoute><RequireChosenOption><NewProjectPage /></RequireChosenOption></PrivateRoute>} />
    <Route path="/projects/:id/edit" element={<ProjectRoute><NewProjectPage /></ProjectRoute>} />
    <Route path="/projects/:id/review" element={<ProjectRoute><TeamReviewPage /></ProjectRoute>} />
    <Route path="/projects/:id/payment" element={<ProjectRoute><PaymentPage /></ProjectRoute>} />
    <Route path="/projects/:id/download" element={<ProjectRoute><DownloadPage /></ProjectRoute>} />
    <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />

    {/* Public share routes — magic-link uses same page, reads magicToken param */}
    <Route path="/share/:token/magic/:magicToken" element={<SharePage />} />
    <Route path="/share/:token" element={<SharePage />} />

    {/* Admin panel — role-gated, desktop only */}
    <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
      <Route index element={<Navigate to="/admin/users" replace />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="users/:id" element={<UserDetailPage />} />
      <Route path="coupons-referrals" element={<CouponsReferralsPage />} />
      <Route path="dashboard" element={<AdminDashboardPage />} />
      <Route path="billing" element={<BillingPage />} />
      <Route path="marketing" element={<MarketingPage />} />
      <Route path="health" element={<HealthPage />} />
      <Route path="data-management" element={<DataManagementPage />} />
      <Route path="b2b-preview" element={<B2BPreviewPage />} />
      <Route path="audit-log" element={<AuditLogPage />} />
      <Route path="orders" element={<OrdersPage />} />
      <Route path="leads" element={<LeadsPage />} />
      <Route path="leads/:id" element={<LeadDetailPage />} />
      <Route path="orders/:id/restoration" element={<RestorationPage />} />
    </Route>

    {/* Contact Us: public, no auth required. Single destination; legacy
        /feedback redirects here. Form internals unchanged (separate brief). */}
    <Route path="/contact" element={<FeedbackPage />} />
    {/* Distinct URL for a confirmed submission — same reasoning as
        /provider-signup/submitted above. */}
    <Route path="/contact/submitted" element={<FeedbackPage submittedRoute />} />
    <Route path="/feedback" element={<Navigate to="/contact" replace />} />

    {/* Catch all: a real "page not found" page, not a silent jump to the homepage
        (search engines report that as a "soft 404"). On a direct visit Vercel has
        already answered with build/404.html and a 404 status; see vercel.json. */}
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

// While a screen's file downloads during in-app navigation, the router keeps the
// previous screen on view (React transitions). This shows on a direct visit whose
// file has not arrived within index.js's wait, and briefly after a sign-in guard lets
// a visitor through (the screen behind it is only fetched then): the same spinner the
// guard shows while it checks the session, so the two run on as one.
const ScreenLoading = () => <div data-screen-loading><LoadingSpinner fullScreen /></div>;

const AppRoutes = () => {
  const { pathname } = useLocation();
  return (
    <ChunkErrorBoundary resetKey={pathname}>
      <Suspense fallback={<ScreenLoading />}>
        {APP_ROUTES}
      </Suspense>
    </ChunkErrorBoundary>
  );
};

// A sign-in guard (PrivateRoute, ProjectRoute, AdminRoute) carries `gatesScreens`:
// what it protects is fetched only once it lets the visitor through, so a signed-out
// visitor is sent to sign-in straight away and never downloads the editor, checkout
// or admin panel (Codex review, PR 425). PublicRoute is not a gate: the sign-in and
// sign-up pages it wraps are what most visitors to those addresses see.
const isGate = (element) => Boolean(element?.type?.gatesScreens);

// The on-demand screens an element tree renders, stopping at a gate.
const screensIn = (node, found = []) => {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child) || isGate(child)) return;
    if (typeof child.type?.preload === 'function') found.push(child.type);
    if (child.props?.children) screensIn(child.props.children, found);
  });
  return found;
};

/**
 * Starts downloading the screen(s) for an address, read from the same <Route> list
 * the app renders (so it can never disagree with it). index.js waits for this before
 * the first render, so a direct visit shows the real page, not a loading state.
 * Resolves to nothing; a failed download is left for the page itself to retry.
 */
export const preloadScreensFor = (pathname) => {
  const matches = matchRoutes(createRoutesFromChildren(APP_ROUTES.props.children), pathname) || [];
  // Nested routes (the admin pages) sit under their parent's gate, so stop at the first.
  const gate = matches.findIndex(m => isGate(m.route.element));
  const open = gate === -1 ? matches : matches.slice(0, gate);
  const screens = open.flatMap(m => screensIn(m.route.element));
  return Promise.all(screens.map(s => s.preload().catch(() => {}))).then(() => undefined);
};

function App() {
  // Vercel Web Analytics injects its own script on mount, outside consent.js's
  // gating (it isn't a `type="text/plain"` tag consent.js knows how to
  // unlock). Mounting it only once analytics consent is granted keeps it
  // from bypassing the banner entirely.
  const { consent } = useConsent();

  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <PageHelpProvider>
        <div className="min-h-screen bg-dark-900">
          <AppRoutes />
          <LeadAttributionLinker />
          <RetroactiveConsentModal />
          <ConsentBanner />
          <ChatWidget />
          <ToastContainer
            position="top-center"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            icon={({ type }) => <ToastIcon type={type} />}
            className="app-toast-container"
            toastOptions={{
              success: { autoClose: 4000 },
              info:    { autoClose: 4000 },
              error:   { autoClose: 8000 },
              warning: { autoClose: 8000 },
            }}
          />
          {consent?.analytics && <Analytics />}
        </div>
        </PageHelpProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
